import type { Issue, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, ProjectDocument } from './document.js';
import { panelSize } from './assembly.js';
import { panelPropertyOnAxis, thicknessAxisOf } from './assemblyEdit.js';

/**
 * Two panels of one assembly that continue each other (same plane, thickness, material and grain,
 * the same cross-section, touching end to end) can become one panel. Every reference to the second
 * one (shelves, drawers, fronts, joints, links) moves to the first, so the generated parts stay where
 * they were.
 */

const TOL = 500; // 0.5 mm

export interface MergeCandidate {
  /** The panel that stays (lower / left / back one). */
  keep: string;
  /** The panel that is joined into it. */
  remove: string;
  axis: 0 | 1 | 2;
}

function extent(p: AssemblyPanel): { min: Vec3; max: Vec3 } {
  const s = panelSize(p);
  return { min: [...p.position], max: [0, 1, 2].map((k) => p.position[k]! + s[k]!) as Vec3 };
}

const plain = (p: AssemblyPanel) => !p.repeat || p.repeat.count <= 1;

/** How `a` and `b` can be merged (null = they cannot). */
export function mergeAxis(a: AssemblyPanel, b: AssemblyPanel): MergeCandidate | null {
  if (a === b || !plain(a) || !plain(b)) return null;
  if (a.orientation !== b.orientation || a.thickness !== b.thickness || a.materialSlot !== b.materialSlot || a.grain !== b.grain) return null;
  const A = extent(a);
  const B = extent(b);
  const t = thicknessAxisOf(a);
  let axis: 0 | 1 | 2 | null = null;
  for (const k of [0, 1, 2] as const) {
    const same = Math.abs(A.min[k] - B.min[k]) <= TOL && Math.abs(A.max[k] - B.max[k]) <= TOL;
    if (same) continue;
    const touch = Math.abs(A.max[k] - B.min[k]) <= TOL || Math.abs(B.max[k] - A.min[k]) <= TOL;
    if (!touch || k === t || axis !== null) return null;
    axis = k;
  }
  if (axis === null) return null;
  const [keep, remove] = A.min[axis] <= B.min[axis] ? [a, b] : [b, a];
  return { keep: keep.key, remove: remove.key, axis };
}

/** Every pair of panels of the assembly that could be one panel. */
export function mergeCandidates(inst: AssemblyInstance): MergeCandidate[] {
  const out: MergeCandidate[] = [];
  const ps = inst.panels;
  for (let i = 0; i < ps.length; i++) for (let j = i + 1; j < ps.length; j++) {
    const m = mergeAxis(ps[i]!, ps[j]!);
    if (m) out.push(m);
  }
  return out;
}

export function mergeIssues(inst: AssemblyInstance): Issue[] {
  const label = (k: string) => inst.panels.find((p) => p.key === k)?.label ?? k;
  return mergeCandidates(inst).map((m) => ({
    severity: 'info' as const,
    code: 'panel.mergeable',
    message: `„${label(m.keep)}” i „${label(m.remove)}” se nastavljaju jedna na drugu — mogu se spojiti u jednu ploču.`,
    cabinetId: inst.id,
    partId: `${inst.id}/${m.keep}`,
    otherPartId: `${inst.id}/${m.remove}`,
  }));
}

/**
 * Joins panel `removeKey` into `keepKey` (they must be a merge candidate). The kept panel grows to
 * cover both; shelves, drawers, fronts, joints and links that pointed at the removed panel point at
 * the kept one. Manual edge settings of the removed panel are dropped. Returns false when the two
 * panels cannot be merged. `doc` (optional) is updated for links.
 */
export function mergePanels(inst: AssemblyInstance, keepKey: string, removeKey: string, doc?: ProjectDocument): boolean {
  const a = inst.panels.find((p) => p.key === keepKey);
  const b = inst.panels.find((p) => p.key === removeKey);
  if (!a || !b) return false;
  const m = mergeAxis(a, b);
  if (!m) return false;
  const k = m.axis;
  const A = extent(a);
  const B = extent(b);
  const lo = Math.min(A.min[k], B.min[k]);
  const hi = Math.max(A.max[k], B.max[k]);
  a.position[k] = lo;
  a[panelPropertyOnAxis(a, k)] = hi - lo;
  const swap = (key: string) => (key === removeKey ? keepKey : key);
  const swapN = (key: string | null) => (key === removeKey ? keepKey : key);
  // Joints: the kept panel takes the removed one's, and joints to the removed one move to it.
  for (const j of b.joints) if (j.key !== keepKey && !a.joints.some((x) => x.key === j.key && x.kind === j.kind)) a.joints.push({ ...j });
  inst.panels = inst.panels.filter((p) => p !== b);
  for (const p of inst.panels) {
    const seen = new Set<string>();
    p.joints = p.joints
      .map((j) => ({ ...j, key: swap(j.key) }))
      .filter((j) => j.key !== p.key && !seen.has(`${j.key}:${j.kind}`) && seen.add(`${j.key}:${j.kind}`));
  }
  for (const s of inst.shelfSets ?? []) {
    s.bottomKey = swap(s.bottomKey);
    s.topKey = swap(s.topKey);
    s.refKey = swap(s.refKey);
    s.leftKey = swapN(s.leftKey);
    s.rightKey = swapN(s.rightKey);
  }
  for (const d of inst.drawerSets ?? []) {
    d.leftKey = swap(d.leftKey);
    d.rightKey = swap(d.rightKey);
    d.bottomKey = swap(d.bottomKey);
    d.topKey = swap(d.topKey);
  }
  for (const f of inst.fronts ?? []) {
    for (const s of f.spaces) {
      s.left = swapN(s.left);
      s.right = swapN(s.right);
      s.bottom = swapN(s.bottom);
      s.top = swapN(s.top);
    }
    if (f.wallsOver) f.wallsOver = [...new Set(f.wallsOver.map(swap))];
  }
  for (const key of Object.keys(inst.edgeOverrides)) if (key.split(':')[0] === removeKey) delete inst.edgeOverrides[key];
  if (doc) {
    for (const l of doc.links) for (const e of [l.a, l.b]) if (e.kind === 'panel' && e.unitId === inst.id && e.panelKey === removeKey) e.panelKey = keepKey;
    doc.links = doc.links.filter((l) => !(l.a.kind === 'panel' && l.b.kind === 'panel' && l.a.unitId === l.b.unitId && l.a.panelKey === l.b.panelKey && l.a.property === l.b.property));
  }
  return true;
}
